// packages/ui/src/components/HealthCheckDiskCard.tsx
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client.js'

interface HealthCheckDiskApproval {
  id: string
  title: string
  status: string
  payload: {
    system: string
    host: string
    pct: number
    mount: string
    investigation: string
    cleanup_commands: string[]
  }
  resolved_by_channel?: string
  resolved_by_display?: string
  action_error?: string
  action_attempt_count?: number
}

export function HealthCheckDiskCard({ approval }: { approval: HealthCheckDiskApproval }) {
  const qc = useQueryClient()

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'skipped') =>
      apiRequest<unknown>(`/approvals/${approval.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const dismiss = useMutation({
    mutationFn: () =>
      apiRequest<unknown>(`/approvals/${approval.id}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const retry = useMutation({
    mutationFn: () =>
      apiRequest<unknown>(`/approvals/${approval.id}/retry`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const isPending = approval.status === 'pending'
  const isInvestigating = approval.status === 'investigating'
  const isFailed = approval.status === 'failed'
  const isRunning = approval.status === 'running'
  const isDone = approval.status === 'done'
  const isResolved = ['skipped', 'timeout', 'dismissed', 'approved', 'running', 'done', 'failed'].includes(approval.status)

  const pct = approval.payload.pct
  const pillClass = pct >= 90 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'

  return (
    <div className="border rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold">🖥️ {approval.payload.system} disk at {pct}%</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${pillClass}`}>
            {approval.status}
          </span>
        </div>
        {isResolved && (
          <span className="text-xs text-gray-500">
            {approval.status} · {approval.resolved_by_channel}
          </span>
        )}
      </div>

      <div className="text-xs text-gray-500">
        {approval.payload.host} • {approval.payload.mount}
      </div>

      {isInvestigating && (
        <div className="text-sm text-gray-500 flex items-center gap-1">
          <span className="animate-spin">⏳</span> Investigating...
        </div>
      )}

      {approval.payload.investigation && (
        <div>
          <div className="text-xs font-semibold text-gray-600 mt-2">Investigation</div>
          <pre className="text-xs bg-gray-50 p-2 mt-1 rounded overflow-auto max-h-32 whitespace-pre-wrap">
            {approval.payload.investigation}
          </pre>
        </div>
      )}

      {approval.payload.cleanup_commands && approval.payload.cleanup_commands.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-600 mt-2">Proposed cleanup</div>
          <ol className="list-decimal list-inside text-xs space-y-1 mt-1">
            {approval.payload.cleanup_commands.map((cmd, i) => (
              <li key={i}>
                <code className="bg-gray-50 px-1 py-0.5 rounded">{cmd}</code>
              </li>
            ))}
          </ol>
        </div>
      )}

      {isRunning && (
        <div className="text-sm text-blue-600 flex items-center gap-1">
          <span className="animate-spin">⚙️</span> Running cleanup... (attempt {approval.action_attempt_count})
        </div>
      )}

      {isDone && (
        <div className="text-sm text-green-600">✅ Cleanup complete</div>
      )}

      {isFailed && (
        <div className="space-y-1">
          <div className="text-sm text-red-600">❌ Cleanup failed: {approval.action_error}</div>
          <button
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
            className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200"
          >
            Retry
          </button>
        </div>
      )}

      {isPending && (
        <div className="flex gap-2">
          <button
            onClick={() => decide.mutate('approved')}
            disabled={decide.isPending}
            className="px-3 py-1 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
          >
            ✅ Approve
          </button>
          <button
            onClick={() => decide.mutate('skipped')}
            disabled={decide.isPending}
            className="px-3 py-1 rounded bg-gray-200 text-gray-700 text-sm hover:bg-gray-300 disabled:opacity-50"
          >
            ❌ Skip
          </button>
          <button
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  )
}
